import { useIsIOS } from '@/components/organisms/OfferListingHero/OfferListingHero.hooks';
import { scrollTo } from '@/helpers/scrollTo';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { createRef, useEffect, useRef, useState } from 'react';
function getDefaultSection(sections) {
    return sections.find((section) => section.status !== 'Hidden')?.key || '#';
}
export function useTrackActiveSection(sections) {
    const containerRef = useRef(null);
    const [containerIsVisible, setContainerIsVisible] = useState(false);
    const [sectionRefs] = useState(() => {
        const refs = {};
        sections.forEach((section) => {
            refs[section.key] = createRef();
        });
        return refs;
    });
    const [activeSection, setActiveSection] = useState(getDefaultSection(sections));
    useEffect(() => {
        const calculateAndSetActiveSection = () => {
            const targetHeight = window.innerHeight / 2;
            const inUseSectionRefs = Object.entries(sectionRefs).filter(([_, ref]) => !!ref.current);
            const activeSection = inUseSectionRefs.reverse().find(([_, ref]) => {
                const sectionTop = ref.current?.getBoundingClientRect().top || 0;
                return sectionTop <= targetHeight;
            });
            const activeSectionKey = activeSection ? activeSection[0] : getDefaultSection(sections);
            setActiveSection(activeSectionKey);
        };
        const controller = new AbortController();
        calculateAndSetActiveSection();
        const resizeObserver = new ResizeObserver(() => calculateAndSetActiveSection());
        window.addEventListener('scroll', calculateAndSetActiveSection, { signal: controller.signal, passive: true });
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }
        return () => {
            controller.abort();
            resizeObserver.disconnect();
        };
    }, []);
    useIntersectionObserver(containerRef, {
        threshold: 0,
        rootMargin: '0px',
    }, (entry) => {
        setContainerIsVisible(entry.isIntersecting);
    });
    return { activeSection, containerRef, sectionRefs, containerIsVisible };
}
export function useManageActiveSectionUrlHash(activeSection, containerIsVisible) {
    useEffect(() => {
        if (!containerIsVisible ||
            activeSection === '#' // We can't put a hash in the URL hash
        ) {
            history.replaceState(null, '', window.location.pathname + window.location.search);
            return;
        }
        history.replaceState(null, '', `#${activeSection}`);
    }, [activeSection, containerIsVisible]);
}
export function useManageSectionFocus(sections, sectionRefs) {
    const [sectionMasonryRefs] = useState(() => {
        const refs = {};
        sections.forEach((section) => {
            refs[section.key] = createRef();
        });
        return refs;
    });
    const isIOS = useIsIOS();
    const focusSection = (sectionKey) => {
        const section = sectionRefs[sectionKey];
        const sectionMasonry = sectionMasonryRefs[sectionKey];
        scrollTo(section.current, {}, () => scrollTo(section.current));
        if (isIOS)
            return;
        sectionMasonry.current?.focusFirstItem?.();
    };
    return { sectionMasonryRefs, focusSection };
}
export const useManageInitialSectionFocus = (focusSection) => {
    useEffect(() => {
        if (window.location.hash.match(/^[#][a-z]$/)) {
            const letter = window.location.hash.replace('#', '');
            focusSection(letter);
        }
    }, []);
};
export function useScrollToActiveNavButton(keys, activeSection) {
    const [navButtonRefs] = useState(() => {
        const refs = {};
        keys.forEach((key) => {
            refs[key] = createRef();
        });
        return refs;
    });
    const navContainerRef = useRef(null);
    useEffect(() => {
        const activeButton = navButtonRefs[activeSection]?.current;
        const navContainer = navContainerRef.current;
        if (!activeButton || !navContainer)
            return;
        const containerRect = navContainer.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();
        // Check if button is outside the visible area of the nav container
        const buttonTop = buttonRect.top - containerRect.top + navContainer.scrollTop;
        const buttonBottom = buttonTop + buttonRect.height;
        const containerScrollTop = navContainer.scrollTop;
        const containerHeight = navContainer.clientHeight;
        if (buttonTop < containerScrollTop || buttonBottom > containerScrollTop + containerHeight) {
            // Center the active button in the nav container with a small delay
            const scrollTo = buttonTop - containerHeight / 2 + buttonRect.height / 2;
            setTimeout(() => {
                navContainer.scrollTo({
                    top: Math.max(0, scrollTo),
                    behavior: 'smooth',
                });
            }, 300);
        }
    }, [activeSection, navButtonRefs]);
    return { navButtonRefs, navContainerRef };
}
